import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useRegisterInput from '@afosto/app-components-react/Form/useRegisterInput';
import ItemsFormSection from '../ItemsFormSection';
import TotalsFormSection from '../TotalsFormSection';
import Form from '@afosto/app-components-react/Form';
import FormActions from '@afosto/app-components-react/Form/FormActions';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import { projection as projectionApi } from '@afosto/order/api/Projection';
import validationSchema from './validations';
import translations from './translations';
import PricingAtFormSection from '../PricingAtFormSection';

/**
 * Fetch an calculation projection.
 * @param {array} items
 * @param {array} adjustments
 * @param {datetime} pricingAt
 * @param {boolean} pricingRules
 * @param {object|null} order
 * @returns {Promise<null>}
 */
const fetchProjection = async (
  { items = [], adjustments = [], pricingAt, applyPricingRules },
  order,
) => {
  try {
    const formattedItems = items.reduce((acc, item) => {
      const filteredAdjustments = (item?.adjustments ?? []).filter(
        adjustment => !adjustment?.result?.is_applied_rule,
      );
      const formattedItem = {
        reference: item.reference,
        sku: item.sku,
        description: item.description,
        quantity: item.quantity,
        amount: item.amount,
        adjustments: filteredAdjustments,
      };

      return [...acc, formattedItem];
    }, []);

    const response = await projectionApi.createProjection(
      {
        client_id: order?.client_id ?? null,
        items: formattedItems,
        adjustments,
        contact_id: order?.settings?.contact?.id ?? null,
        organisation_id: order?.settings?.organisation?.id ?? null,
        address_id: order?.settings?.billing?.address_id ?? null,
        pricing_at: pricingAt ?? null,
        settings: {
          billing: order?.settings?.billing,
          shipping: order?.settings?.shipping,
        },
      },
      { rules: applyPricingRules === false ? 0 : 1 },
    );
    const { data = null } = response;

    return data;
  } catch (err) {
    return null;
  }
};

const CalculationForm = ({ items, onCancel, onSubmit, order }) => {
  const intl = useIntl();
  const [isFetchingProjection, setIsFetchingProjection] = useState(false);
  const [projection, setProjection] = useState({
    items: [],
    adjustments: [],
    vat: [],
    subtotal: 0,
    total: 0,
    pricingAt: null,
  });
  const isReturnOrder = order?.type === 'return';
  const form = useForm({
    validationSchema,
    defaultValues: {
      items: items ?? [],
      adjustments: [],
      pricingAt: null,
      applyPricingRules: !isReturnOrder,
    },
    onSubmit,
    onCancel,
  });
  const { getValues, watch } = form;
  const itemsValue = watch('items');
  const adjustmentsValue = watch('adjustments');
  const pricingAtValue = watch('pricingAt');
  const applyPricingRulesValue = watch('applyPricingRules');

  useRegisterInput('items', true, form);
  useRegisterInput('adjustments', true, form);

  useEffect(() => {
    const fetchLatestProjection = async () => {
      try {
        setIsFetchingProjection(true);

        const values = getValues();

        if (isReturnOrder) {
          values.applyPricingRules = false;
        }

        const newProjection = await fetchProjection(values, order);
        setProjection(newProjection);
      } catch (err) {
        setProjection(null);
      } finally {
        setIsFetchingProjection(false);
      }
    };

    if (itemsValue.length > 0) {
      fetchLatestProjection();
    }
  }, [
    order,
    isReturnOrder,
    itemsValue,
    adjustmentsValue,
    pricingAtValue,
    applyPricingRulesValue,
    getValues,
  ]);

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form} renderActions={false}>
        <PricingAtFormSection showApplyPricingRulesOption={!isReturnOrder} />
        <ItemsFormSection
          projection={projection}
          isFetchingProjection={isFetchingProjection}
          allowCreate={false}
          allowRemove={false}
          allowChangeQuantity={false}
        />
        <TotalsFormSection projection={projection} isFetchingProjection={isFetchingProjection} />
        <FormActions
          primaryAction={{
            label: intl.formatMessage(translations.formActions.save),
            onClick: form.onSubmit,
            type: 'submit',
            color: 'primary',
            variant: 'contained',
            disabled: itemsValue.length === 0,
          }}
          secondaryAction={
            onCancel
              ? {
                  label: intl.formatMessage(translations.formActions.cancel),
                  onClick: form.onCancel,
                  variant: 'contained',
                }
              : null
          }
        />
      </Form>
    </LoadingWrapper>
  );
};

CalculationForm.propTypes = {
  items: PropTypes.array,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  order: PropTypes.object.isRequired,
};

CalculationForm.defaultProps = {
  items: [],
  onCancel: undefined,
  onSubmit: undefined,
};

export default CalculationForm;
